import {
  type ThemeRegistry,
  themeRegistryListSchema,
} from '../schema';

export const THEME_REGISTRIES = themeRegistryListSchema.parse([
  {
    id: 'theme_trust_neutral',
    label: 'Trust Neutral',
    description: '最も安全で汎用的な標準テーマ。',
    version: 1,
    status: 'active',
    priority: 100,
    tags: ['trust', 'neutral', 'fallback'],
    visualTone: 'trust',
    impressionKeywords: ['誠実', '静か', '安心感', '整理された'],
    compatibleFamilies: [
      'btob_inquiry',
      'regional_service_inquiry',
      'education_application',
      'recruitment_entry',
    ],
    discouragedFamilies: ['d2c_purchase'],
    forbiddenFamilies: [],
    fontFamily: 'Noto Sans JP',
    tokens: {
      text: '#202123',
      textMuted: '#6b7280',
      background: '#ffffff',
      surface: '#f7f7f8',
      surfaceMuted: '#f3f4f6',
      border: '#e5e7eb',
      primary: '#202123',
      secondary: '#4b5563',
    },
    typography: {
      headingWeight: 'bold',
      bodyWeight: 'normal',
      radiusScale: 'medium',
      spacingScale: 'normal',
    },
    informationDensityProfile: {
      preferredDensities: ['light', 'standard'],
      contentWidth: 'normal',
      maxCardsPerRow: 3,
      maxInlineActions: 2,
      sectionSpacingBias: 'balanced',
    },
    componentRules: {
      buttonStyle: 'solid',
      borderEmphasis: 'soft',
      shadowLevel: 'none',
      backgroundTreatment: 'subtle_surface',
    },
    visualAssetPolicy: {
      photoUsage: 'optional',
      diagramUsage: 'optional',
      uiCaptureUsage: 'avoid',
      cropStyle: 'balanced',
      notes: [
        '写真が弱い場合でも成立することを優先する',
        '図解は補助に留める',
      ],
    },
    emphasisPriority: ['copy', 'proof', 'cta', 'visual'],
    materialSensitivity: 'low',
    weakMaterialFallback: [
      {
        condition: 'weak_proof',
        action: 'prefer_surface_sections',
        notes: ['実績を大きく見せず、落ち着いた面の中で補助的に扱う'],
      },
      {
        condition: 'weak_review',
        action: 'downgrade_cta_emphasis',
        notes: ['信頼要素が弱い時は CTA を煽りすぎない'],
      },
    ],
    ctaHarmony: {
      maxPrimaryEmphasis: 'normal',
      allowBannerCta: false,
      allowStickyMobileCta: false,
      notes: ['誠実さを優先し、強い押し出しは避ける'],
    },
    preferredSectionTemplateIds: [
      'proof_metric_cards',
      'proof_badges_awards',
      'cta_simple_center',
      'faq_accordion',
      'reasons_icon_list',
    ],
    discouragedSectionTemplateIds: [
      'review_carousel',
      'cta_banner_emphasis',
      'benefit_future_visual',
      'features_visual_explain',
    ],
    forbiddenSectionTemplateIds: ['cta_sticky_mobile'],
    prohibitedPatterns: [
      'Hero で過剰なビジュアル演出を主役にする',
      'review を proof の代替として強く見せる',
      '強いセール感のある banner CTA を使う',
    ],
    sectionMoodRules: {
      hero: ['過剰な背景演出を入れず、文字の読みやすさを優先'],
      proof: ['数字や資格バッジを落ち着いた面で支える'],
      cta: ['強く煽らず、明確に押せる状態を作る'],
      form: ['枠線と余白で安心感を出す'],
    },
    usageNotes: [
      '迷ったらこれ',
      'safe mode 時の最優先',
      'review や proof の実在性が弱い案件でも違和感が出にくい',
    ],
  },
  {
    id: 'theme_corporate_clean',
    label: 'Corporate Clean',
    description: '法人向けの端正さと説明のしやすさを重視するテーマ。',
    version: 1,
    status: 'active',
    priority: 95,
    tags: ['corporate', 'b2b', 'clean'],
    visualTone: 'corporate',
    impressionKeywords: ['端正', '論理的', '信頼感', '説明しやすい'],
    compatibleFamilies: ['btob_inquiry', 'saas_ai_inquiry', 'recruitment_entry'],
    discouragedFamilies: ['store_reservation', 'd2c_purchase'],
    forbiddenFamilies: [],
    fontFamily: 'Noto Sans JP',
    tokens: {
      text: '#1f2937',
      textMuted: '#6b7280',
      background: '#fcfdff',
      surface: '#f8fafc',
      surfaceMuted: '#f1f5f9',
      border: '#d7dee7',
      primary: '#1f2937',
      secondary: '#475569',
    },
    typography: {
      headingWeight: 'bold',
      bodyWeight: 'medium',
      radiusScale: 'small',
      spacingScale: 'normal',
    },
    informationDensityProfile: {
      preferredDensities: ['standard', 'rich'],
      contentWidth: 'wide',
      maxCardsPerRow: 4,
      maxInlineActions: 2,
      sectionSpacingBias: 'compact',
    },
    componentRules: {
      buttonStyle: 'solid',
      borderEmphasis: 'normal',
      shadowLevel: 'none',
      backgroundTreatment: 'flat',
    },
    visualAssetPolicy: {
      photoUsage: 'optional',
      diagramUsage: 'recommended',
      uiCaptureUsage: 'optional',
      cropStyle: 'strict',
      notes: [
        '写真よりも比較表や図解を優先しやすい',
        'ビジュアルは説明補助として使う',
      ],
    },
    emphasisPriority: ['copy', 'proof', 'visual', 'cta'],
    materialSensitivity: 'medium',
    weakMaterialFallback: [
      {
        condition: 'missing_ui_capture',
        action: 'prefer_textual_layout',
        notes: ['UI前提の見せ方を弱め、feature list と copy で支える'],
      },
      {
        condition: 'weak_proof',
        action: 'prefer_surface_sections',
        notes: ['ロゴや数字が弱い時は metric の主役化を避ける'],
      },
    ],
    ctaHarmony: {
      maxPrimaryEmphasis: 'normal',
      allowBannerCta: false,
      allowStickyMobileCta: false,
      notes: ['社内共有しやすい落ち着いた CTA を優先する'],
    },
    preferredSectionTemplateIds: [
      'problem_b2b_structured',
      'comparison_table',
      'feature_list_compact',
      'proof_metric_cards',
      'faq_two_columns',
    ],
    discouragedSectionTemplateIds: [
      'review_speech_bubbles',
      'review_carousel',
      'problem_story_intro',
      'cta_banner_emphasis',
    ],
    forbiddenSectionTemplateIds: ['cta_sticky_mobile'],
    prohibitedPatterns: [
      'カジュアルすぎる吹き出しレビューを主役化する',
      '感情訴求を論理訴求より前に出しすぎる',
      'CTA だけ色やサイズで浮かせすぎる',
    ],
    sectionMoodRules: {
      hero: ['見出しと補足の論理階層をはっきり分ける'],
      proof: ['metric / logo / comparison と相性が良い'],
      cta: ['ボタン文言を具体的に、色数は増やさない'],
      form: ['入力項目の密度をやや高めにしても成立'],
    },
    usageNotes: [
      'desktop_first との相性が良い',
      '比較表や feature list が多い案件に向く',
      'story 訴求を主役にするとやや硬すぎる',
    ],
  },
  {
    id: 'theme_modern_trust',
    label: 'Modern Trust',
    description: '先進性を出しつつ、信頼感を維持するテーマ。',
    version: 1,
    status: 'active',
    priority: 90,
    tags: ['modern', 'saas', 'product'],
    visualTone: 'modern',
    impressionKeywords: ['先進的', 'クリーン', 'シャープ', '信頼感'],
    compatibleFamilies: ['saas_ai_inquiry', 'btob_inquiry', 'education_application'],
    discouragedFamilies: ['store_reservation', 'regional_service_inquiry'],
    forbiddenFamilies: [],
    fontFamily: 'Noto Sans JP',
    tokens: {
      text: '#0f172a',
      textMuted: '#64748b',
      background: '#f8fbff',
      surface: '#f8fafc',
      surfaceMuted: '#eef4ff',
      border: '#dbe4f0',
      primary: '#1d4ed8',
      secondary: '#334155',
    },
    typography: {
      headingWeight: 'extrabold',
      bodyWeight: 'normal',
      radiusScale: 'medium',
      spacingScale: 'normal',
    },
    informationDensityProfile: {
      preferredDensities: ['standard'],
      contentWidth: 'normal',
      maxCardsPerRow: 3,
      maxInlineActions: 2,
      sectionSpacingBias: 'balanced',
    },
    componentRules: {
      buttonStyle: 'solid',
      borderEmphasis: 'soft',
      shadowLevel: 'soft',
      backgroundTreatment: 'subtle_surface',
    },
    visualAssetPolicy: {
      photoUsage: 'optional',
      diagramUsage: 'recommended',
      uiCaptureUsage: 'recommended',
      cropStyle: 'strict',
      notes: [
        'UIキャプチャの質が全体印象を左右する',
        '図解や product visual を使う場合も過剰演出は避ける',
      ],
    },
    emphasisPriority: ['visual', 'copy', 'proof', 'cta'],
    materialSensitivity: 'high',
    weakMaterialFallback: [
      {
        condition: 'missing_ui_capture',
        action: 'switch_theme_candidate',
        notes: ['UIが弱い場合は theme_corporate_clean か theme_trust_neutral を優先する'],
      },
      {
        condition: 'low_quality_photo',
        action: 'downgrade_visual_emphasis',
        notes: ['visual を主役にしすぎず、copy に重心を戻す'],
      },
      {
        condition: 'weak_proof',
        action: 'downgrade_cta_emphasis',
        notes: ['信頼素材が弱いのに強い訴求にしない'],
      },
    ],
    ctaHarmony: {
      maxPrimaryEmphasis: 'normal',
      allowBannerCta: false,
      allowStickyMobileCta: false,
      notes: ['テック感よりも信頼感を優先し、CTAだけ浮かせない'],
    },
    preferredSectionTemplateIds: [
      'feature_list_ui_rows',
      'solution_visual_diagram',
      'use_cases_industry_examples',
      'proof_metric_cards',
      'comparison_table',
    ],
    discouragedSectionTemplateIds: [
      'review_speech_bubbles',
      'review_with_photo',
      'reasons_short_bullets',
      'cta_banner_emphasis',
    ],
    forbiddenSectionTemplateIds: ['cta_sticky_mobile', 'proof_photo_case'],
    prohibitedPatterns: [
      '低品質 UI キャプチャを hero 主役にする',
      '暖色の強い banner CTA でテック感を壊す',
      'review / people 写真で生活感を前に出しすぎる',
    ],
    sectionMoodRules: {
      hero: ['先進性を出すが、背景は薄い面差に留める'],
      proof: ['数字やロゴをクリーンに整理する'],
      cta: ['primary を明確にし、secondary は控えめにする'],
      form: ['フォーム自体は neutral に保つ'],
    },
    usageNotes: [
      'UIキャプチャがある案件と相性が良い',
      '青を強くしすぎると安っぽくなるので、彩度は抑える',
      'modern でも white base は崩さない',
    ],
  },
  {
    id: 'theme_bright_friendly',
    label: 'Bright Friendly',
    description: '親しみやすさと軽やかさを作るテーマ。',
    version: 1,
    status: 'active',
    priority: 80,
    tags: ['bright', 'friendly', 'application'],
    visualTone: 'bright_clean',
    impressionKeywords: ['明るい', '親しみやすい', '軽やか', '参加しやすい'],
    compatibleFamilies: ['seminar_application', 'education_application', 'store_reservation'],
    discouragedFamilies: ['btob_inquiry', 'saas_ai_inquiry'],
    forbiddenFamilies: [],
    fontFamily: 'Noto Sans JP',
    tokens: {
      text: '#1f2937',
      textMuted: '#6b7280',
      background: '#fffdfa',
      surface: '#fffaf5',
      surfaceMuted: '#fff1e6',
      border: '#f1dfd0',
      primary: '#ea580c',
      secondary: '#9a3412',
    },
    typography: {
      headingWeight: 'bold',
      bodyWeight: 'normal',
      radiusScale: 'medium',
      spacingScale: 'spacious',
    },
    informationDensityProfile: {
      preferredDensities: ['light', 'standard'],
      contentWidth: 'normal',
      maxCardsPerRow: 3,
      maxInlineActions: 2,
      sectionSpacingBias: 'spacious',
    },
    componentRules: {
      buttonStyle: 'solid',
      borderEmphasis: 'soft',
      shadowLevel: 'soft',
      backgroundTreatment: 'subtle_surface',
    },
    visualAssetPolicy: {
      photoUsage: 'recommended',
      diagramUsage: 'optional',
      uiCaptureUsage: 'avoid',
      cropStyle: 'natural',
      notes: [
        '写真がなくても面と色で明るさを出せるようにする',
        '図解は軽い補助用途に留める',
      ],
    },
    emphasisPriority: ['cta', 'copy', 'visual', 'proof'],
    materialSensitivity: 'medium',
    weakMaterialFallback: [
      {
        condition: 'missing_photo',
        action: 'prefer_surface_sections',
        notes: ['写真前提にせず、軽い面構成で明るさを出す'],
      },
      {
        condition: 'weak_review',
        action: 'downgrade_cta_emphasis',
        notes: ['friendly でも押し売りにしない'],
      },
    ],
    ctaHarmony: {
      maxPrimaryEmphasis: 'strong',
      allowBannerCta: true,
      allowStickyMobileCta: true,
      notes: [
        '予約や申込み系では背中押しを許容する',
        '安売り感のある強色多用は避ける',
      ],
    },
    preferredSectionTemplateIds: [
      'benefit_cards',
      'benefit_bullets',
      'overview_cards',
      'target_audience_checklist',
      'cta_banner_emphasis',
    ],
    discouragedSectionTemplateIds: [
      'comparison_table',
      'problem_b2b_structured',
      'message_text_block',
      'proof_badges_awards',
    ],
    forbiddenSectionTemplateIds: ['proof_metric_cards'],
    prohibitedPatterns: [
      '明るさを彩度だけで出す',
      '値引きっぽい CTA で安売り感を出す',
      '法人向け比較表を主役にして硬く見せる',
    ],
    sectionMoodRules: {
      hero: ['明るく見せるが、ポップに振りすぎない'],
      proof: ['実績より安心感・参加しやすさ寄りの見せ方にする'],
      cta: ['行動を促すが押し売りにはしない'],
      form: ['入力フォームは白背景を保って可読性を優先'],
    },
    usageNotes: [
      '現行 ポップ の安全版として使う',
      '色は暖色寄りでも、背景全面を色で埋めない',
      'D2C の強い売り込みにはやや弱い',
    ],
  },
  {
    id: 'theme_natural_warm',
    label: 'Natural Warm',
    description: '地域密着や生活者向けの温かい安心感を作るテーマ。',
    version: 1,
    status: 'active',
    priority: 85,
    tags: ['natural', 'warm', 'regional'],
    visualTone: 'natural_warm',
    impressionKeywords: ['やさしい', '生活者向け', '地域密着', '相談しやすい'],
    compatibleFamilies: ['regional_service_inquiry', 'store_reservation', 'education_application'],
    discouragedFamilies: ['saas_ai_inquiry'],
    forbiddenFamilies: [],
    fontFamily: 'Noto Sans JP',
    tokens: {
      text: '#292524',
      textMuted: '#78716c',
      background: '#fdfbf8',
      surface: '#faf6f1',
      surfaceMuted: '#f5efe7',
      border: '#e8ded2',
      primary: '#57534e',
      secondary: '#a8a29e',
    },
    typography: {
      headingWeight: 'bold',
      bodyWeight: 'normal',
      radiusScale: 'medium',
      spacingScale: 'spacious',
    },
    informationDensityProfile: {
      preferredDensities: ['light', 'standard'],
      contentWidth: 'normal',
      maxCardsPerRow: 3,
      maxInlineActions: 2,
      sectionSpacingBias: 'spacious',
    },
    componentRules: {
      buttonStyle: 'solid',
      borderEmphasis: 'soft',
      shadowLevel: 'none',
      backgroundTreatment: 'subtle_surface',
    },
    visualAssetPolicy: {
      photoUsage: 'recommended',
      diagramUsage: 'avoid',
      uiCaptureUsage: 'avoid',
      cropStyle: 'natural',
      notes: [
        '人物や現場写真と相性が良い',
        '図解よりも写真と文章の温度感を優先する',
      ],
    },
    emphasisPriority: ['copy', 'visual', 'cta', 'proof'],
    materialSensitivity: 'medium',
    weakMaterialFallback: [
      {
        condition: 'missing_photo',
        action: 'prefer_textual_layout',
        notes: ['温かみを写真頼みではなく copy と余白で出す'],
      },
      {
        condition: 'weak_review',
        action: 'prefer_surface_sections',
        notes: ['感情訴求を控えめにし、相談のしやすさを前に出す'],
      },
    ],
    ctaHarmony: {
      maxPrimaryEmphasis: 'normal',
      allowBannerCta: false,
      allowStickyMobileCta: true,
      notes: ['相談導線は強くできるが、画面全体で温度差をつけすぎない'],
    },
    preferredSectionTemplateIds: [
      'review_speech_bubbles',
      'review_with_photo',
      'reasons_icon_list',
      'services_menu_blocks',
      'faq_cards',
      'access_simple_text',
      'message_text_block',
    ],
    discouragedSectionTemplateIds: [
      'comparison_table',
      'feature_list_ui_rows',
      'solution_visual_diagram',
      'proof_metric_cards',
    ],
    forbiddenSectionTemplateIds: ['cta_sticky_mobile'],
    prohibitedPatterns: [
      '生活感の強い写真を無加工で hero 主役にする',
      'テック系図解で温度感を壊す',
      '数字実績を強く見せすぎて冷たくする',
    ],
    sectionMoodRules: {
      hero: ['写真がある場合も余白で整え、生活感を出しすぎない'],
      proof: ['権威より安心感を支える使い方が向く'],
      cta: ['相談導線、来店導線と相性が良い'],
      form: ['フォームの境界を柔らかく見せる'],
    },
    usageNotes: [
      '現行 やわらかめ に近い',
      'review や people 写真と合わせやすい',
      'かわいい 方向には寄せない',
    ],
  },
  {
    id: 'theme_premium_minimal',
    label: 'Premium Minimal',
    description: '高単価・上質・厳選感を出すテーマ。',
    version: 1,
    status: 'active',
    priority: 78,
    tags: ['premium', 'minimal', 'high-end'],
    visualTone: 'premium',
    impressionKeywords: ['上質', '静かな高級感', '厳選感', '余白重視'],
    compatibleFamilies: ['d2c_purchase', 'regional_service_inquiry', 'store_reservation'],
    discouragedFamilies: ['seminar_application', 'education_application'],
    forbiddenFamilies: [],
    fontFamily: 'Noto Sans JP',
    tokens: {
      text: '#1c1917',
      textMuted: '#78716c',
      background: '#fcfbf8',
      surface: '#f7f3ee',
      surfaceMuted: '#f1ebe3',
      border: '#ddd4c8',
      primary: '#3f3f46',
      secondary: '#78716c',
    },
    typography: {
      headingWeight: 'extrabold',
      bodyWeight: 'normal',
      radiusScale: 'small',
      spacingScale: 'spacious',
    },
    informationDensityProfile: {
      preferredDensities: ['light', 'standard'],
      contentWidth: 'narrow',
      maxCardsPerRow: 2,
      maxInlineActions: 1,
      sectionSpacingBias: 'spacious',
    },
    componentRules: {
      buttonStyle: 'solid',
      borderEmphasis: 'soft',
      shadowLevel: 'none',
      backgroundTreatment: 'flat',
    },
    visualAssetPolicy: {
      photoUsage: 'hero_dominant',
      diagramUsage: 'avoid',
      uiCaptureUsage: 'avoid',
      cropStyle: 'strict',
      notes: [
        '写真品質が低い場合はテーマ変更を優先する',
        '図解や情報過多で上質感を壊さない',
      ],
    },
    emphasisPriority: ['visual', 'copy', 'proof', 'cta'],
    materialSensitivity: 'high',
    weakMaterialFallback: [
      {
        condition: 'low_quality_photo',
        action: 'switch_theme_candidate',
        notes: ['写真品質が弱い場合は theme_trust_neutral か theme_natural_warm に落とす'],
      },
      {
        condition: 'weak_proof',
        action: 'prefer_textual_layout',
        notes: ['上質感を証拠の量で見せようとしない'],
      },
      {
        condition: 'missing_photo',
        action: 'downgrade_visual_emphasis',
        notes: ['余白と text で成立する構成に寄せる'],
      },
    ],
    ctaHarmony: {
      maxPrimaryEmphasis: 'soft',
      allowBannerCta: false,
      allowStickyMobileCta: false,
      notes: ['CTA を強くしすぎると一気に安っぽくなるため、静かな強さに留める'],
    },
    preferredSectionTemplateIds: [
      'proof_photo_case',
      'benefit_image_rows',
      'message_with_photo',
      'offer_limited_banner',
    ],
    discouragedSectionTemplateIds: [
      'faq_cards',
      'comparison_checklist',
      'reasons_short_bullets',
      'cta_dual_buttons',
    ],
    forbiddenSectionTemplateIds: ['cta_sticky_mobile', 'review_carousel', 'logo_strip'],
    prohibitedPatterns: [
      '低品質写真を hero 全面に使う',
      '複数 CTA を並列に並べて売り込み感を出す',
      '安価なキャンペーンバナー風の見せ方をする',
    ],
    sectionMoodRules: {
      hero: ['文字量を絞り、余白で品質を見せる'],
      proof: ['数を並べすぎず、選び抜いた証拠だけ見せる'],
      cta: ['強い赤やオレンジは使わず、静かに押せる状態を作る'],
      form: ['フォームも上質に見せるが、視認性は落とさない'],
    },
    usageNotes: [
      '現行 高級感 の整理版',
      '価格訴求よりブランド訴求に強い',
      'offer_push を強く使う D2C では、別 section で背中押しを補う必要がある',
    ],
  },
  {
    id: 'theme_formal_trust',
    label: 'Formal Trust',
    description: '堅実さと専門性を優先するテーマ。',
    version: 1,
    status: 'active',
    priority: 82,
    tags: ['formal', 'trust', 'professional'],
    visualTone: 'trust',
    impressionKeywords: ['堅実', '専門的', '誤解がない', '落ち着いている'],
    compatibleFamilies: ['regional_service_inquiry', 'btob_inquiry', 'recruitment_entry'],
    discouragedFamilies: ['d2c_purchase', 'store_reservation'],
    forbiddenFamilies: [],
    fontFamily: 'Noto Sans JP',
    tokens: {
      text: '#1f2937',
      textMuted: '#6b7280',
      background: '#fcfcfd',
      surface: '#f6f7f9',
      surfaceMuted: '#eef1f4',
      border: '#d7dbe1',
      primary: '#374151',
      secondary: '#6b7280',
    },
    typography: {
      headingWeight: 'bold',
      bodyWeight: 'medium',
      radiusScale: 'small',
      spacingScale: 'normal',
    },
    informationDensityProfile: {
      preferredDensities: ['standard'],
      contentWidth: 'normal',
      maxCardsPerRow: 3,
      maxInlineActions: 1,
      sectionSpacingBias: 'balanced',
    },
    componentRules: {
      buttonStyle: 'outline',
      borderEmphasis: 'normal',
      shadowLevel: 'none',
      backgroundTreatment: 'flat',
    },
    visualAssetPolicy: {
      photoUsage: 'optional',
      diagramUsage: 'optional',
      uiCaptureUsage: 'avoid',
      cropStyle: 'strict',
      notes: [
        '写真がある場合も証明写真や現場写真のように整った見え方を優先する',
        'visual は補助で、専門性の明確さを主役にする',
      ],
    },
    emphasisPriority: ['proof', 'copy', 'cta', 'visual'],
    materialSensitivity: 'low',
    weakMaterialFallback: [
      {
        condition: 'weak_proof',
        action: 'prefer_textual_layout',
        notes: ['権威素材が弱い場合は、説明の明確さで補う'],
      },
      {
        condition: 'weak_review',
        action: 'downgrade_cta_emphasis',
        notes: ['感情訴求不足を CTA の強さで補わない'],
      },
    ],
    ctaHarmony: {
      maxPrimaryEmphasis: 'soft',
      allowBannerCta: false,
      allowStickyMobileCta: false,
      notes: ['outline や低彩度 primary を許容し、誠実さを維持する'],
    },
    preferredSectionTemplateIds: [
      'proof_badges_awards',
      'proof_metric_cards',
      'faq_two_columns',
      'message_text_block',
      'problem_b2b_structured',
    ],
    discouragedSectionTemplateIds: [
      'review_speech_bubbles',
      'review_carousel',
      'cta_banner_emphasis',
      'benefit_future_visual',
    ],
    forbiddenSectionTemplateIds: ['cta_sticky_mobile'],
    prohibitedPatterns: [
      'カジュアルなレビュー演出で専門性を壊す',
      '強い販促バナーで誠実さを壊す',
      'visual を主役にして authority を弱める',
    ],
    sectionMoodRules: {
      hero: ['主張を誇張しすぎない'],
      proof: ['資格、認定、監修との相性が良い'],
      cta: ['相談導線を誠実に見せる'],
      form: ['事務的でわかりやすい印象を保つ'],
    },
    usageNotes: [
      '現行 フォーマル に近い',
      'outline CTA を許容する数少ないテーマ',
      'warm な写真訴求を主役にすると硬く見えやすい',
    ],
  },
  {
    id: 'theme_regional_chain',
    label: 'Regional Chain',
    description: '地域密着型サービス（不用品回収・引越し・便利屋等）の全国チェーン向けテーマ。深緑×黄色の親しみと信頼感を両立。',
    version: 1,
    status: 'active',
    priority: 88,
    tags: ['regional', 'service_chain', 'inquiry'],
    visualTone: 'trust',
    impressionKeywords: ['親しみやすい', '安心感', '信頼', '相談しやすい', '元気'],
    compatibleFamilies: ['regional_service_inquiry'],
    discouragedFamilies: ['saas_ai_inquiry', 'd2c_purchase'],
    forbiddenFamilies: [],
    fontFamily: 'Noto Sans JP',
    tokens: {
      text: '#0f2e26',
      textMuted: '#4d6660',
      background: '#ffffff',
      surface: '#f1faf5',
      surfaceMuted: '#e5f5ec',
      border: '#cfe7d8',
      primary: '#1f7a5e',
      secondary: '#f4c116',
    },
    typography: {
      headingWeight: 'bold',
      bodyWeight: 'normal',
      radiusScale: 'medium',
      spacingScale: 'normal',
    },
    informationDensityProfile: {
      preferredDensities: ['standard', 'rich'],
      contentWidth: 'normal',
      maxCardsPerRow: 3,
      maxInlineActions: 2,
      sectionSpacingBias: 'balanced',
    },
    componentRules: {
      buttonStyle: 'solid',
      borderEmphasis: 'soft',
      shadowLevel: 'soft',
      backgroundTreatment: 'subtle_surface',
    },
    visualAssetPolicy: {
      photoUsage: 'recommended',
      diagramUsage: 'optional',
      uiCaptureUsage: 'avoid',
      cropStyle: 'natural',
      notes: [
        '作業員や現場写真でリアルさを出す',
        'ロゴチェーンの信頼感を視覚で支える',
      ],
    },
    emphasisPriority: ['cta', 'copy', 'proof', 'visual'],
    materialSensitivity: 'low',
    weakMaterialFallback: [
      {
        condition: 'missing_photo',
        action: 'prefer_textual_layout',
        notes: ['写真なくても色とアイコンで安心感を作る'],
      },
    ],
    ctaHarmony: {
      maxPrimaryEmphasis: 'strong',
      allowBannerCta: true,
      allowStickyMobileCta: true,
      notes: ['電話番号と相談導線を強く繰り返す前提'],
    },
    preferredSectionTemplateIds: [
      'hero_regional_service',
      'flow_horizontal_icons',
      'pricing_color_cards',
      'message_warning_alert',
    ],
    discouragedSectionTemplateIds: [
      'comparison_table',
      'feature_list_ui_rows',
      'solution_visual_diagram',
    ],
    forbiddenSectionTemplateIds: [],
    prohibitedPatterns: [
      'モノトーンの硬い印象に寄せる',
      '高単価ブランドのような抑制された見せ方',
    ],
    sectionMoodRules: {
      hero: ['電話番号と複数CTAを大きく置く前提'],
      proof: ['店舗数・対応エリア数で安心を作る'],
      cta: ['電話・LINE・メールの複数導線を並列で出す'],
      form: ['敷居を低く、シンプルに'],
    },
    usageNotes: [
      '不用品回収、引越し、便利屋、家事代行などのチェーン型',
      'カジュアル過ぎず、信頼感を保つ',
      '電話CTAが主軸になりやすい',
    ],
  },
]);

export const themeRegistryMap = new Map(
  THEME_REGISTRIES.map((theme) => [theme.id, theme] as const),
);

export function getThemeRegistry(themeId: string): ThemeRegistry | undefined {
  return themeRegistryMap.get(themeId);
}
